import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';
import { safeWrite } from '@/utils/supabaseWrite';
import CTAButton from '@/components/ui/CTAButton';
import ProgressBar from '@/components/ui/ProgressBar';

const PHASE_CARDS = [
  { title: 'path_b_phase1_title', weeks: 'path_b_phase1_weeks', body: 'path_b_phase1_body', color: '#7A5A6A' },
  { title: 'path_b_phase2_title', weeks: 'path_b_phase2_weeks', body: 'path_b_phase2_body', color: '#4a6a80' },
  { title: 'path_b_phase3_title', weeks: 'path_b_phase3_weeks', body: 'path_b_phase3_body', color: '#8B4A5A' },
];

const titleStyle = {
  fontFamily: 'var(--font-title)', fontStyle: 'italic', fontWeight: 500,
  fontSize: 34, color: 'var(--dark-brown)',
  lineHeight: 1.12, letterSpacing: '-0.015em', marginBottom: 22,
  fontVariationSettings: '"opsz" 96',
};

const bodyStyle = {
  fontFamily: 'var(--font-body)', fontSize: 17, color: 'var(--text)',
  lineHeight: 1.7, marginBottom: 16,
};

const optionBtn = {
  width: '100%', padding: '16px 18px', borderRadius: 14, minHeight: 56,
  border: '1.5px solid var(--primary)', background: 'var(--surface)',
  fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--text)',
  fontWeight: 500, letterSpacing: '-0.005em',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  boxShadow: '0 2px 10px rgba(45, 26, 31, 0.04)',
  transition:
    'background 180ms ease, border-color 180ms ease, transform 120ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 220ms ease',
};

export default function PathBOrientation() {
  const { user, lang, refreshProgress } = useApp();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [practiceState, setPracticeState] = useState('pending'); // pending | felt | unsure
  const [loading, setLoading] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const pct = el.scrollTop / (el.scrollHeight - el.clientHeight);
    setScrollPct(Math.min(1, Math.max(0, pct)));
  }, []);

  const handleComplete = useCallback(async () => {
    setLoading(true);
    try {
      const saved = localStorage.getItem('bb7');
      const data = saved ? JSON.parse(saved) : {};
      data.path_b_orientation_complete = true;
      await safeWrite(user?.id, data);
      await refreshProgress();
    } catch (err) {
      console.error('Failed to save orientation:', err);
    }
    navigate('/home', { replace: true });
  }, [user, navigate, refreshProgress]);

  const showCTA = practiceState !== 'pending';

  return (
    <div style={{ background: 'var(--nude)', minHeight: '100dvh', maxWidth: 520, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
      {/* Gold rule top */}
      <div style={{ height: 3, background: 'var(--gold)' }} />

      {/* Progress bar */}
      <div style={{ padding: '8px 24px 0' }}>
        <ProgressBar value={scrollPct} max={1} color="var(--gold)" />
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: '32px 24px 120px' }}>

        {/* Section 1: Why release first */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={titleStyle}>{t('path_b_orientation_title', lang)}</h1>
          <p style={bodyStyle}>{t('path_b_orientation_body_1', lang)}</p>
          <p style={bodyStyle}>{t('path_b_orientation_body_2', lang)}</p>
          <p style={{ ...bodyStyle, fontStyle: 'italic', color: 'var(--dark-brown)' }}>
            {t('path_b_orientation_body_3', lang)}
          </p>
        </div>

        {/* Section 2: Three phases */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PHASE_CARDS.map(card => (
              <div key={card.title} style={{
                background: 'var(--cream)', borderRadius: 12, padding: 16,
                borderLeft: `4px solid ${card.color}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontFamily: 'var(--font-medium)', fontSize: 14, color: card.color }}>
                    {t(card.title, lang)}
                  </span>
                  <span style={{ fontFamily: 'var(--font-light)', fontSize: 11, color: 'var(--warm-brown)' }}>
                    {t(card.weeks, lang)}
                  </span>
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                  {t(card.body, lang)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Practice */}
        <div style={{ marginBottom: 32 }}>
          <div style={{
            fontFamily: 'var(--font-medium)', fontSize: 11, color: 'var(--gold)',
            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8,
          }}>
            {t('path_b_practice_label', lang)}
          </div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text)',
            lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 24,
          }}>
            {t('path_b_practice_body', lang)}
          </div>

          {practiceState === 'pending' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" onClick={() => setPracticeState('felt')} style={optionBtn}>
                {t('path_b_practice_felt', lang)}
              </button>
              <button type="button" onClick={() => setPracticeState('unsure')} style={{ ...optionBtn, borderColor: 'var(--beige)' }}>
                {t('path_b_practice_unsure', lang)}
              </button>
            </div>
          )}

          {practiceState === 'unsure' && (
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--warm-brown)',
              fontStyle: 'italic', marginTop: 12,
            }}>
              {t('path_b_practice_reassure', lang)}
            </div>
          )}
        </div>

        {/* CTA */}
        {showCTA && (
          <CTAButton
            label={loading ? '...' : t('path_b_orientation_cta', lang)}
            onClick={handleComplete}
            disabled={loading}
          />
        )}
      </div>

      {/* Gold rule bottom */}
      <div style={{ height: 3, background: 'var(--gold)' }} />
    </div>
  );
}

